// src/features/chat/hooks/useChatSenderProfiles.js
import { useEffect, useMemo, useRef, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase';
import { isAssistantMessage } from '../models/chatModel';

/**
 * Subscribe to sender profile data (displayName, profilePictureUrl) for chat messages.
 * Uses lightweight, view-scoped listeners so profile updates propagate while the chat is open.
 *
 * @param {Array} messages - Normalized chat messages
 * @returns {{ profiles: Object, loading: boolean }} Map of userId -> profile
 */
export function useChatSenderProfiles(messages = []) {
  const senderIds = useMemo(() => {
    const ids = new Set();
    for (const msg of messages) {
      if (!msg?.senderId) continue;
      if (isAssistantMessage(msg)) continue;
      ids.add(msg.senderId);
    }
    return Array.from(ids).sort();
  }, [messages]);

  const senderKey = useMemo(() => senderIds.join(','), [senderIds]);
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(false);
  const listenersRef = useRef(new Map());
  const pendingRef = useRef(new Set());
  const retryStateRef = useRef(new Map());

  useEffect(() => {
    const listeners = listenersRef.current;
    const pending = pendingRef.current;
    const retryState = retryStateRef.current;

    if (!senderKey) {
      for (const unsubscribe of listeners.values()) {
        unsubscribe();
      }
      listeners.clear();
      pending.clear();
      for (const { timer } of retryState.values()) {
        clearTimeout(timer);
      }
      retryState.clear();
      setProfiles({});
      setLoading(false);
      return;
    }

    const ids = senderKey.split(',').filter(Boolean);
    const desired = new Set(ids);

    const scheduleRetry = (id) => {
      const current = retryState.get(id) || { attempt: 0, timer: null };
      const nextAttempt = current.attempt + 1;
      const backoff = Math.min(10000, 500 * 2 ** (nextAttempt - 1));
      if (current.timer) {
        clearTimeout(current.timer);
      }
      const timer = setTimeout(() => {
        retryState.set(id, { attempt: nextAttempt, timer: null });
        if (desired.has(id)) {
          subscribeToProfile(id);
        }
      }, backoff);
      retryState.set(id, { attempt: nextAttempt, timer });
    };

    const subscribeToProfile = (id) => {
      if (!desired.has(id)) return;

      pending.add(id);
      setLoading(true);

      const unsubscribe = onSnapshot(
        doc(db, 'users', id),
        (snapshot) => {
          const data = snapshot.exists() ? snapshot.data() : null;
          const nextProfile = data
            ? {
                displayName: data.displayName || null,
                profilePictureUrl: data.profilePictureUrl || null,
              }
            : null;

          setProfiles((prev) => {
            const prevProfile = prev[id];
            if (
              prevProfile?.displayName === nextProfile?.displayName &&
              prevProfile?.profilePictureUrl === nextProfile?.profilePictureUrl
            ) {
              return prev;
            }
            return { ...prev, [id]: nextProfile };
          });

          pending.delete(id);
          const retryEntry = retryState.get(id);
          if (retryEntry?.timer) {
            clearTimeout(retryEntry.timer);
          }
          retryState.delete(id);
          if (pending.size === 0) {
            setLoading(false);
          }
        },
        (error) => {
          console.error(`Error subscribing to profile for ${id}:`, error);
          pending.delete(id);
          const existing = listeners.get(id);
          if (existing) {
            existing();
          }
          listeners.delete(id);
          scheduleRetry(id);
          if (pending.size === 0) {
            setLoading(false);
          }
          setProfiles((prev) => (id in prev ? prev : { ...prev, [id]: null }));
        }
      );

      listeners.set(id, unsubscribe);
    };

    ids.forEach((id) => {
      if (listeners.has(id)) return;
      subscribeToProfile(id);
    });

    if (pending.size === 0) {
      setLoading(false);
    }

    for (const [id, unsubscribe] of listeners.entries()) {
      if (desired.has(id)) continue;
      unsubscribe();
      listeners.delete(id);
      pending.delete(id);
      const retryEntry = retryState.get(id);
      if (retryEntry?.timer) {
        clearTimeout(retryEntry.timer);
      }
      retryState.delete(id);
      setProfiles((prev) => {
        if (!(id in prev)) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }

    for (const [id, entry] of retryState.entries()) {
      if (desired.has(id)) continue;
      if (entry?.timer) {
        clearTimeout(entry.timer);
      }
      retryState.delete(id);
    }
  }, [senderKey]);

  useEffect(() => {
    const listeners = listenersRef.current;
    const pending = pendingRef.current;
    const retryState = retryStateRef.current;

    return () => {
      for (const unsubscribe of listeners.values()) {
        unsubscribe();
      }
      for (const { timer } of retryState.values()) {
        clearTimeout(timer);
      }
      listeners.clear();
      pending.clear();
      retryState.clear();
    };
  }, []);

  return { profiles, loading };
}

export default useChatSenderProfiles;
